/* eslint-disable react-hooks/rules-of-hooks */

/* eslint-disable max-len */
import { Checkbox, Form } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { ILeiData, ILeiSubory } from 'types/leiTypes';

import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import InputTextArea from 'components/atoms/InputTextArea';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import UploadComponent, { IFileList } from 'components/atoms/UploadComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import { IcisStaty } from 'services/cisStaty';
import fileDownload from 'services/fileDownload';
import checkLeiApi from 'services/lei/checkLEI';
import prepareFile from 'services/lei/fileDownload';

import { EAnoNe } from 'enums/anoNe';
import { EPeriodType, ETypDatMatky, ETypMatky } from 'enums/lei/leiDataEnums';
import { EMatNemaDuvod } from 'enums/lei/matNemaDuvod';
import { EMaterskaSpol } from 'enums/lei/materskaSpol';

import anoNe from 'data/codebooks/anoNeSelect';
import { leiKonsolidaceSouborySelect } from 'data/codebooks/lei/leiKonsolidaceSouborySelect';
import { leiKonsolidaceTypSelectAlt } from 'data/codebooks/lei/leiKonsolidaceTypSelect';
import { leiMaMaterskouSpolFinalSelect } from 'data/codebooks/lei/leiMaMaterskouSpolSelect';
import { leiMatNelzeDuvodSelect } from 'data/codebooks/lei/leiMatNelzeDuvodSelect';
import { leiMatNemaDuvodSelect } from 'data/codebooks/lei/leiMatNemaDuvodSelect';
import { leiRejstrikyPermanent, leiRejstrikySelect } from 'data/codebooks/lei/leiRejstrikySelect';

import { _getKeyValue_ } from 'utils/getObjectKeyValue';

interface IFinalniMatka {
    finalniMatkaData?: ILeiData['matky'];
    primaMatkaData?: ILeiData['matky'];
    primaMatkaStav?: EMaterskaSpol;
    // getCountryText: (country: string) => string | undefined;
    disabled?: boolean;
    // getRegionText: (country: string, region: string) => string | undefined;
    setFormData?: React.Dispatch<React.SetStateAction<ILeiData | undefined>>;
    isEdit?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    upravaForm?: any;
    cisStaty: IcisStaty[];
    areFilesEqual?: boolean;
    setAreFilesEqual?: React.Dispatch<React.SetStateAction<boolean>>;
    setUploading?: React.Dispatch<React.SetStateAction<boolean>>;
}

const FinalniMatka: React.FC<IFinalniMatka> = ({
    finalniMatkaData,
    primaMatkaData,
    primaMatkaStav,
    // getCountryText,
    // getRegionText,
    isEdit,
    setFormData,
    upravaForm,
    disabled,
    cisStaty,
    areFilesEqual,
    setAreFilesEqual,
    setUploading
}) => {
    const { lei } = useContext(Context);
    const [firstRender, setFirstRender] = useState<boolean>(true);
    const [leiCheckLoading, setLeiCheckLoading] = useState<boolean>(false);
    const [leiCheckError, setLeiCheckError] = useState<boolean>(false);

    /*  const hqLangValues =
        finalniMatkaData && finalniMatkaData.length > 0
            ? finalniMatkaData[0].matkabezlei?.adresy &&
              finalniMatkaData[0].matkabezlei.adresy.filter(e => e.typadresy === ETypAdresy.HEADQUARTERSADDRES)
            : [];
*/
    const finalniMatkaDates = {
        accounting:
            finalniMatkaData && finalniMatkaData.length > 0
                ? finalniMatkaData[0].informacekmatce &&
                  finalniMatkaData[0].informacekmatce.relationshipperiod?.filter(
                      e => e.periodtype === EPeriodType.ACCOUNTING_PERIOD
                  )[0]
                : undefined,
        document:
            finalniMatkaData && finalniMatkaData.length > 0
                ? finalniMatkaData[0].informacekmatce &&
                  finalniMatkaData[0].informacekmatce.relationshipperiod?.filter(
                      e => e.periodtype === EPeriodType.DOCUMENT_FILING_PERIOD
                  )[0]
                : undefined,
        relationship:
            finalniMatkaData && finalniMatkaData.length > 0
                ? finalniMatkaData[0].informacekmatce &&
                  finalniMatkaData[0].informacekmatce.relationshipperiod?.filter(
                      e => e.periodtype === EPeriodType.RELATIONSHIP_PERIOD
                  )[0]
                : undefined
    };

    const setForceRerender = useState<boolean>(false)[1];
    const rerender = () => {
        setForceRerender(val => !val);
    };

    const checkLei = (leiCislo: string) => {
        setLeiCheckLoading(true);
        checkLeiApi(leiCislo)
            .then(data =>
                upravaForm?.setFieldsValue({
                    fleinazev: data.spolecnost
                })
            )
            .catch(() => setLeiCheckError(true))
            .finally(() => setLeiCheckLoading(false));
    };

    useEffect(() => {
        upravaForm?.setFieldsValue({
            fduvod: finalniMatkaData && finalniMatkaData.length > 0 && finalniMatkaData[0].duvod,
            fmalei:
                finalniMatkaData && finalniMatkaData.length > 0
                    ? finalniMatkaData[0].matkaslei
                        ? 'ano'
                        : finalniMatkaData[0].matkabezlei && 'ne'
                    : undefined,
            fvalidationdocuments:
                finalniMatkaData &&
                finalniMatkaData.length > 0 &&
                finalniMatkaData[0].informacekmatce?.validationdocuments,
            frelationship: dayjs(finalniMatkaDates.relationship?.startdate)
        });
        finalniMatkaData &&
            finalniMatkaData.length > 0 &&
            finalniMatkaData[0].matkaslei &&
            checkLei(finalniMatkaData[0].matkaslei);
        setTimeout(() => rerender(), 0); // rerender po nastaveni default values (inak sa niektore nezobrazia)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [prilozeneSoubory, setPrilozeneSoubory] = useState<ILeiSubory[]>(
        finalniMatkaData && finalniMatkaData.length > 0 && finalniMatkaData[0].informacekmatce?.soubory
            ? finalniMatkaData[0].informacekmatce.soubory
            : []
    );

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
        isEdit === false &&
            prilozeneSoubory.length !==
                (finalniMatkaData && finalniMatkaData.length > 0 && finalniMatkaData[0].informacekmatce?.soubory
                    ? finalniMatkaData[0].informacekmatce.soubory
                    : []
                ).length &&
            setFormData &&
            setFormData(pv => {
                return {
                    ...pv,
                    matky: [
                        ...(pv && pv.matky && pv.matky.length > 0
                            ? pv.matky.map(e =>
                                  e.typmatky !== ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY
                                      ? e
                                      : {
                                            ...e,
                                            informacekmatce: {
                                                ...e.informacekmatce,
                                                soubory: prilozeneSoubory
                                            }
                                        }
                              )
                            : [])
                    ]
                };
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prilozeneSoubory]);

    useEffect(() => {
        !firstRender && upravaForm.validateFields(['fsoubor']);
        firstRender && setFirstRender(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [areFilesEqual]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [fileList, setFileList] = useState<IFileList[] | undefined>(
        (finalniMatkaData && finalniMatkaData.length > 0 && finalniMatkaData[0].informacekmatce?.soubory
            ? finalniMatkaData[0].informacekmatce.soubory
            : []
        ).map((e, i) => {
            return {
                uid: String(i),
                name: e.orignazev ? e.orignazev : 'bez nazvu',
                fileName: e.tempnazev ? e.tempnazev : 'a',
                type: e.orignazev ? e.orignazev.split('.').slice(-1)[0] : '',
                size: 0,
                status: 'done'
            };
        })
    );

    const uploadProps = {
        showDownloadIcon: true,
        onChange: (data: UploadChangeParam) => {
            if (data.file.status === 'uploading' && setUploading) setUploading(true);
            if (data.file.status !== 'uploading' && setUploading) setUploading(false);
            if (data.fileList.length) {
                if (data.file.response) {
                    upravaForm?.validateFields(['fsoubor']);
                    setPrilozeneSoubory(
                        data.fileList
                            .filter(f => f.status && f.status !== 'error')
                            .map(e => {
                                return {
                                    orignazev: e.name.normalize(),
                                    tempnazev: e.response ? e.response.tempnazev : e.fileName
                                };
                            })
                    );
                }
                setFileList(
                    data.fileList
                        .filter(f => f.status && f.status !== 'error')
                        .map(e => {
                            return {
                                uid: e.uid,
                                name: e.name.normalize(),
                                fileName: e.fileName ? e.fileName : e.response ? e.response.tempnazev : '',
                                type: e.type!,
                                size: e.size!,
                                status: e.status ? e.status : 'error'
                            };
                        })
                );
            }
        },
        onRemove: (file: UploadFile) => {
            if (fileList?.length) {
                const oldState = [...fileList];
                const newState = oldState.filter(e => e.uid !== file.uid);
                setFileList(newState);
                const newSoubory = [...prilozeneSoubory].filter(e => e.tempnazev !== file.fileName);
                setPrilozeneSoubory(newSoubory);
            }
        },
        onDownload: (file: UploadFile) =>
            prepareFile(file.fileName ? file.fileName : '', lei?.idVarsymZadosti?.varsym || '').then(resp => {
                fileDownload(resp.redirect, file.name ? file.name : 'file');
            }),
        fileList: fileList,
        buttonDisabled: fileList ? fileList.length > 4 : false
    };

    return (
        <FormWrapper outForm={upravaForm} onFinish={() => {}} layoutPosition="horizontal">
            <SelectComponent
                notRequired={true}
                disabled={true}
                isFormItem={true}
                name="fstav"
                defaultValue={
                    disabled
                        ? EMaterskaSpol.NE
                        : (finalniMatkaData &&
                              finalniMatkaData.length > 0 &&
                              (finalniMatkaData[0].stav === EMaterskaSpol.ANO_SAME
                                  ? primaMatkaStav
                                  : finalniMatkaData[0].stav)) ||
                          EMaterskaSpol.NE
                }
                labelParagraph="LEI_FINAL_MASPOL_SELECT_ALT"
                data={
                    disabled
                        ? [
                              {
                                  labelcz: 'LEI_MATKA_KONSOLIDOVANA_NEUVEST',
                                  value: EMaterskaSpol.NE
                              }
                          ]
                        : [
                              {
                                  labelcz: 'LEI_MATKA_KONSOLIDOVANA_UVEST',
                                  value: EMaterskaSpol.ANO
                              },
                              {
                                  labelcz: 'LEI_MATKA_KONSOLIDOVANA_NEUVEST',
                                  value: EMaterskaSpol.NE
                              },
                              ...leiMaMaterskouSpolFinalSelect
                          ]
                }
            />
            {
                <>
                    {finalniMatkaData &&
                        finalniMatkaData.length > 0 &&
                        (finalniMatkaData[0].stav === EMaterskaSpol.ANO ||
                            finalniMatkaData[0].stav === EMaterskaSpol.ANO_SAME) &&
                        !finalniMatkaData[0].duvod && (
                            <>
                                <SelectComponent
                                    notRequired={true}
                                    disabled={true}
                                    isFormItem={true}
                                    name="fmalei"
                                    defaultValue={finalniMatkaData && finalniMatkaData[0].matkaslei ? 'ano' : 'ne'}
                                    labelParagraph="LEI_FINAL_MASPOL_MALEI"
                                    data={anoNe}
                                />
                                {(finalniMatkaData && finalniMatkaData[0].matkaslei ? 'ano' : 'ne') === EAnoNe.ANO && (
                                    <LoadingWrapper loading={leiCheckLoading} loadingText="NACITANI_DAT">
                                        <InputComponent
                                            notRequired={true}
                                            disabled={true}
                                            label="LEI_DETAIL_MATER_SPOLOCNOSTI_FIN"
                                            name="fmatkaslei"
                                            initialValue={finalniMatkaData && finalniMatkaData[0].matkaslei}
                                            extramessage={leiCheckError ? 'LEI_CHECK_ERROR' : ''}
                                        />
                                        {upravaForm?.getFieldValue('fleinazev') && (
                                            <InputComponent
                                                notRequired={true}
                                                disabled={true}
                                                label="LEI_DETAIL_MATER_NAZEV_FINAL"
                                                name="fleinazev"
                                                rules={[]}
                                                // placeholder=""
                                                // initialValue={''}
                                            />
                                        )}
                                    </LoadingWrapper>
                                )}
                                {(finalniMatkaData && finalniMatkaData[0].matkaslei ? 'ano' : 'ne') === EAnoNe.NE &&
                                    !finalniMatkaData[0].duvod && (
                                        <>
                                            <SelectComponent
                                                notRequired={true}
                                                disabled={true}
                                                isFormItem={true}
                                                name="fjurisdikce"
                                                defaultValue={
                                                    finalniMatkaData && finalniMatkaData[0].matkabezlei?.jurisdikce
                                                }
                                                labelParagraph="LEI_DETAIL_JURISDICKE"
                                                data={cisStaty}
                                            />
                                            <SelectComponent
                                                notRequired={true}
                                                disabled={true}
                                                isFormItem={true}
                                                showOptionValue={true}
                                                optionValueFilter={['RA888888']}
                                                name="fregistrationauthorityid"
                                                defaultValue={
                                                    finalniMatkaData &&
                                                    finalniMatkaData[0].matkabezlei?.registrationauthorityid
                                                }
                                                labelParagraph="LEI_DETAIL_REJSTRIK"
                                                data={[
                                                    ...(finalniMatkaData &&
                                                    finalniMatkaData[0].matkabezlei?.jurisdikce &&
                                                    Object.keys(leiRejstrikySelect).includes(
                                                        finalniMatkaData[0].matkabezlei?.jurisdikce
                                                    )
                                                        ? _getKeyValue_(finalniMatkaData[0].matkabezlei.jurisdikce)(
                                                              leiRejstrikySelect
                                                          )
                                                        : []),
                                                    ...leiRejstrikyPermanent
                                                ]}
                                            />

                                            {finalniMatkaData &&
                                                finalniMatkaData[0].matkabezlei?.otherregistrationauthorityid && (
                                                    <InputComponent
                                                        notRequired={true}
                                                        disabled={true}
                                                        label="LEI_REJSTRIK_INY"
                                                        name="fotherregistrationauthorityid"
                                                        initialValue={
                                                            finalniMatkaData &&
                                                            finalniMatkaData[0].matkabezlei
                                                                ?.otherregistrationauthorityid
                                                        }
                                                    />
                                                )}
                                            {finalniMatkaData &&
                                                finalniMatkaData[0].matkabezlei?.registrationauthorityentityid && (
                                                    <InputComponent
                                                        notRequired={true}
                                                        disabled={true}
                                                        label="LEI_FINAL_ID_KONSOL"
                                                        name="fregistrationauthorityentityid"
                                                        initialValue={
                                                            finalniMatkaData &&
                                                            finalniMatkaData[0].matkabezlei
                                                                ?.registrationauthorityentityid
                                                        }
                                                        rules={[]}
                                                    />
                                                )}
                                        </>
                                    )}
                                {finalniMatkaData && finalniMatkaData[0].matkaslei && (
                                    <>
                                        <DatePickerComponent
                                            notRequired={true}
                                            disabled={true}
                                            disabledDate={curr => curr > dayjs()}
                                            label="LEI_DETAIL_MATKA_DATUM"
                                            name="frelationship"
                                            initialValue={
                                                finalniMatkaDates && finalniMatkaDates.relationship
                                                    ? dayjs(finalniMatkaDates.relationship.startdate)
                                                    : undefined
                                            }
                                        />
                                        <InputNumberComponent
                                            horizontalLayout={true}
                                            notRequired={true}
                                            disabled={true}
                                            label="LEI_DETAIL_MATKA_PODIL"
                                            name="fpodilspolecnosti"
                                            initialValue={
                                                finalniMatkaData &&
                                                finalniMatkaData[0].informacekmatce?.podilspolecnosti
                                            }
                                        />
                                        <SelectComponent
                                            notRequired={true}
                                            disabled={true}
                                            isFormItem={true}
                                            name="fkonsolidace"
                                            defaultValue={
                                                finalniMatkaData && finalniMatkaData[0].informacekmatce?.konsolidace
                                            }
                                            labelParagraph="LEI_DETAIL_MATKA_KONSOLIDACE"
                                            data={leiKonsolidaceTypSelectAlt}
                                        />

                                        {isEdit === false
                                            ? finalniMatkaData[0].stav !== EMaterskaSpol.ANO_SAME && (
                                                  <>
                                                      <Form.Item
                                                          name="fsoubor"
                                                          label={<Paragraph text={'SOUBOR'} strong={true} />}
                                                          rules={[
                                                              {
                                                                  required: !areFilesEqual,
                                                                  validator() {
                                                                      try {
                                                                          if (
                                                                              (!fileList || fileList.length < 1) &&
                                                                              !areFilesEqual
                                                                          )
                                                                              return Promise.reject(
                                                                                  <TranslateText>
                                                                                      NEVALIDNI_POVINNA_POLOZKA_UPLOAD
                                                                                  </TranslateText>
                                                                              );
                                                                          return Promise.resolve();
                                                                      } catch {
                                                                          return Promise.reject();
                                                                      }
                                                                  }
                                                              }
                                                          ]}
                                                      >
                                                          <div
                                                              style={{
                                                                  position: 'relative',
                                                                  top: -10,
                                                                  marginBottom: 20
                                                              }}
                                                          >
                                                              {primaMatkaData?.length &&
                                                                  primaMatkaData[0].matkaslei ===
                                                                      finalniMatkaData[0].matkaslei && (
                                                                      <div
                                                                          style={{
                                                                              marginBottom: 8,
                                                                              marginTop: 8,
                                                                              marginRight: 40,
                                                                              fontWeight: 600
                                                                          }}
                                                                      >
                                                                          <Checkbox
                                                                              style={{
                                                                                  position: 'relative',
                                                                                  top: -2,
                                                                                  left: 4,
                                                                                  transform: 'scale(1.2)'
                                                                              }}
                                                                              checked={areFilesEqual}
                                                                              onChange={ev => {
                                                                                  setAreFilesEqual &&
                                                                                      setAreFilesEqual(
                                                                                          ev.target.checked
                                                                                      );
                                                                              }}
                                                                          />
                                                                          <Paragraph
                                                                              text="LEI_MAT_SOUBOR_STEJNY"
                                                                              style={{
                                                                                  position: 'relative',
                                                                                  left: 14,
                                                                                  bottom: 2
                                                                              }}
                                                                          />
                                                                      </div>
                                                                  )}
                                                              {!areFilesEqual && (
                                                                  <UploadComponent
                                                                      {...uploadProps}
                                                                      accept={'.pdf, .rtf, .docx, .doc'}
                                                                      buttonId="finalniMatkaPrehledUploadBtn"
                                                                  />
                                                              )}
                                                          </div>
                                                      </Form.Item>
                                                  </>
                                              )
                                            : fileList &&
                                              fileList.length > 0 && (
                                                  <Form.Item
                                                      name="fsoubor"
                                                      label={<Paragraph text={'SOUBOR'} strong={true} />}
                                                  >
                                                      <div
                                                          style={{
                                                              marginBottom: 22
                                                          }}
                                                      >
                                                          <UploadComponent
                                                              buttonId="finalniMatkaPrehledUploadEdit"
                                                              {...{
                                                                  showRemoveIcon: false,
                                                                  onDownload: (file: UploadFile) =>
                                                                      prepareFile(
                                                                          file.fileName ? file.fileName : '',
                                                                          lei?.idVarsymZadosti?.varsym || ''
                                                                      ).then(resp => {
                                                                          fileDownload(
                                                                              resp.redirect,
                                                                              file.name ? file.name : 'file'
                                                                          );
                                                                      }),
                                                                  hideButton: true
                                                              }}
                                                              fileList={fileList}
                                                              accept={'.pdf, .rtf, .docx, .doc'}
                                                          />
                                                      </div>
                                                  </Form.Item>
                                              )}
                                        {isEdit === false &&
                                            !areFilesEqual &&
                                            (finalniMatkaData[0].stav !== EMaterskaSpol.ANO_SAME ||
                                                isEqual(primaMatkaData, finalniMatkaData)) && (
                                                <>
                                                    <SelectComponent
                                                        isFormItem={true}
                                                        name="fvalidationdocuments"
                                                        defaultValue={
                                                            finalniMatkaData &&
                                                            finalniMatkaData[0].informacekmatce?.validationdocuments
                                                        }
                                                        labelParagraph="LEI_DETAIL_MATKA_KONSOLIDACE_CONFIRM"
                                                        data={leiKonsolidaceSouborySelect}
                                                        onChange={() => rerender()}
                                                    />
                                                    {upravaForm?.getFieldValue('fvalidationdocuments') ===
                                                        ETypDatMatky.REGULATORY_FILING && (
                                                        <InputComponent
                                                            label="LEI_DETAIL_REGULATORY"
                                                            name="fvalidationreference"
                                                            initialValue={
                                                                finalniMatkaData &&
                                                                finalniMatkaData[0].informacekmatce?.validationreference
                                                            }
                                                            onChange={() => rerender()}
                                                            maxLength={100}
                                                        />
                                                    )}
                                                    {upravaForm?.getFieldValue('fvalidationdocuments') && (
                                                        <>
                                                            <DatePickerComponent
                                                                disabledDate={current =>
                                                                    upravaForm?.getFieldValue('faccountingend') &&
                                                                    current >
                                                                        dayjs(
                                                                            upravaForm?.getFieldValue('faccountingend')
                                                                        ).subtract(1, 'days')
                                                                }
                                                                label="LEI_DETAIL_UCETNI_OBDOBY_OD"
                                                                name="faccountingstart"
                                                                initialValue={
                                                                    finalniMatkaDates && finalniMatkaDates.accounting
                                                                        ? dayjs(finalniMatkaDates.accounting.startdate)
                                                                        : undefined
                                                                }
                                                            />
                                                            <DatePickerComponent
                                                                disabledDate={current =>
                                                                    upravaForm?.getFieldValue('faccountingstart') &&
                                                                    current <
                                                                        dayjs(
                                                                            upravaForm?.getFieldValue(
                                                                                'faccountingstart'
                                                                            )
                                                                        ).add(1, 'days')
                                                                }
                                                                label="LEI_DETAIL_UCETNI_OBDOBY_DO"
                                                                name="faccountingend"
                                                                initialValue={
                                                                    finalniMatkaDates && finalniMatkaDates.accounting
                                                                        ? finalniMatkaDates.accounting.enddate &&
                                                                          dayjs(finalniMatkaDates.accounting.enddate)
                                                                        : undefined
                                                                }
                                                                notRequired={
                                                                    upravaForm.getFieldValue('fvalidationdocuments') !==
                                                                        ETypDatMatky.ACCOUNTS_FILING &&
                                                                    upravaForm.getFieldValue('fvalidationdocuments') !==
                                                                        ETypDatMatky.REGULATORY_FILING
                                                                }
                                                            />{' '}
                                                        </>
                                                    )}
                                                    {upravaForm?.getFieldValue('fvalidationdocuments') &&
                                                        upravaForm?.getFieldValue('fvalidationdocuments') !==
                                                            ETypDatMatky.ACCOUNTS_FILING && (
                                                            <>
                                                                <DatePickerComponent
                                                                    disabledDate={current =>
                                                                        upravaForm?.getFieldValue('fdocumentend') &&
                                                                        current >
                                                                            dayjs(
                                                                                upravaForm?.getFieldValue(
                                                                                    'fdocumentend'
                                                                                )
                                                                            ).subtract(1, 'days')
                                                                    }
                                                                    label="LEI_DETAIL_PLATNOSTDOKUMENTU_OD"
                                                                    name="fdocumentstart"
                                                                    initialValue={
                                                                        finalniMatkaDates && finalniMatkaDates.document
                                                                            ? dayjs(
                                                                                  finalniMatkaDates.document
                                                                                      .startdate || undefined
                                                                              )
                                                                            : undefined
                                                                    }
                                                                />
                                                                <DatePickerComponent
                                                                    disabledDate={current =>
                                                                        upravaForm?.getFieldValue('fdocumentstart') &&
                                                                        current <
                                                                            dayjs(
                                                                                upravaForm?.getFieldValue(
                                                                                    'fdocumentstart'
                                                                                )
                                                                            ).add(1, 'days')
                                                                    }
                                                                    label="LEI_DETAIL_PLATNOSTDOKUMENTU_DO"
                                                                    name="fdocumentend"
                                                                    initialValue={
                                                                        finalniMatkaDates &&
                                                                        finalniMatkaDates.document?.enddate
                                                                            ? dayjs(finalniMatkaDates.document.enddate)
                                                                            : undefined
                                                                    }
                                                                    notRequired={true}
                                                                />
                                                            </>
                                                        )}
                                                </>
                                            )}
                                        {isEdit !== false && (
                                            <>
                                                <SelectComponent
                                                    notRequired={true}
                                                    disabled={true}
                                                    isFormItem={true}
                                                    name="fvalidationdocuments"
                                                    defaultValue={
                                                        finalniMatkaData &&
                                                        finalniMatkaData[0].informacekmatce?.validationdocuments
                                                    }
                                                    labelParagraph="LEI_DETAIL_MATKA_KONSOLIDACE_CONFIRM"
                                                    data={leiKonsolidaceSouborySelect}
                                                />
                                                {finalniMatkaData &&
                                                    finalniMatkaData[0].informacekmatce?.validationdocuments ===
                                                        ETypDatMatky.REGULATORY_FILING && (
                                                        <InputComponent
                                                            notRequired={true}
                                                            disabled={true}
                                                            label="LEI_DETAIL_REGULATORY"
                                                            name="fvalidationreference"
                                                            initialValue={
                                                                finalniMatkaData &&
                                                                finalniMatkaData[0].informacekmatce?.validationreference
                                                            }
                                                        />
                                                    )}
                                                {finalniMatkaData &&
                                                    finalniMatkaData[0].informacekmatce?.validationdocuments && (
                                                        <InputComponent
                                                            notRequired={true}
                                                            disabled={true}
                                                            label="LEI_DETAIL_UCETNI_OBDOBY"
                                                            name="faccountingstart"
                                                            initialValue={`${
                                                                dayjs(finalniMatkaDates.accounting?.startdate).format(
                                                                    'DD.MM.YYYY'
                                                                ) + ' -'
                                                            } ${
                                                                finalniMatkaDates.accounting?.enddate
                                                                    ? dayjs(
                                                                          finalniMatkaDates.accounting?.enddate
                                                                      ).format('DD.MM.YYYY')
                                                                    : ''
                                                            }`}
                                                        />
                                                    )}
                                                {finalniMatkaData &&
                                                    finalniMatkaData[0].informacekmatce?.validationdocuments &&
                                                    finalniMatkaData &&
                                                    finalniMatkaData[0].informacekmatce?.validationdocuments !==
                                                        ETypDatMatky.ACCOUNTS_FILING && (
                                                        <InputComponent
                                                            notRequired={true}
                                                            disabled={true}
                                                            label="LEI_DETAIL_PLATNOSTDOKUMENTU"
                                                            name="fdocumentstart"
                                                            initialValue={`${
                                                                dayjs(finalniMatkaDates.document?.startdate).format(
                                                                    'DD.MM.YYYY'
                                                                ) + ' -'
                                                            } ${
                                                                finalniMatkaDates.document?.enddate
                                                                    ? dayjs(finalniMatkaDates.document?.enddate).format(
                                                                          'DD.MM.YYYY'
                                                                      )
                                                                    : ''
                                                            }`}
                                                        />
                                                    )}
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    {finalniMatkaData && finalniMatkaData[0].duvod && (
                        <SelectComponent
                            notRequired={true}
                            disabled={true}
                            isFormItem={true}
                            name="fduvod"
                            defaultValue={finalniMatkaData && finalniMatkaData[0].duvod}
                            labelParagraph="LEI_DETAIL_DUVOD_SELECT"
                            data={[
                                ...leiMatNelzeDuvodSelect,
                                ...leiMatNemaDuvodSelect,
                                {
                                    labelcz: 'NON_PUBLIC',
                                    value: EMatNemaDuvod.NON_PUBLIC
                                }
                            ]}
                        />
                    )}
                    {((finalniMatkaData &&
                        finalniMatkaData.length > 0 &&
                        finalniMatkaData[0].stav === EMaterskaSpol.NELZE_UVEST) ||
                        primaMatkaStav === EMaterskaSpol.NELZE_UVEST) && (
                        <>
                            {/*  <SelectComponent
                                    notRequired={true}
                                    disabled={true}
                                    isFormItem={true}
                                    name="fduvod"
                                    defaultValue={finalniMatkaData && finalniMatkaData[0].duvod}
                                    labelParagraph="LEI_DETAIL_DUVOD_SELECT"
                                    data={leiMatNelzeDuvodSelect}
                              /> */}
                            <InputTextArea
                                rows={4}
                                name="fexceptionreference"
                                initialValue={finalniMatkaData && finalniMatkaData[0].exceptionreference}
                                label="LEI_DETAIL_NELZEUVEST_INPUT"
                                maxLength={1000}
                                disabled={true}
                            />
                        </>
                    )}
                </>
            }
        </FormWrapper>
    );
};

export default FinalniMatka;
